import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiContentSave, mdiDelete, mdiDownload, mdiPencilOutline } from '@mdi/js';
import { ComponentType, ComponentTypeEnum, CharacteristicCurve } from '../../types';
import { apiService } from '../../services/apiService';
import { FileUploadZone } from '../components/FileUploadZone';
import { ManualDataEditor } from '../components/ManualDataEditor';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CoolingDeltaTChart } from '../components/CoolingDeltaTChart';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';
import { FlowPressureChart } from '../../components/charts/FlowPressureChart';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${adminTheme.colors.textSecondary};
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: #f1f3f4;
  }
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  flex: 1;
`;

const ActionBtn = styled.button<{ $danger?: boolean; $primary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${p => p.$danger ? adminTheme.colors.danger : p.$primary ? adminTheme.colors.primary : adminTheme.colors.border};
  background: ${p => p.$danger ? adminTheme.colors.danger : p.$primary ? adminTheme.colors.primary : 'white'};
  color: ${p => (p.$danger || p.$primary) ? 'white' : adminTheme.colors.text};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  padding: 20px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  background: white;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  min-height: 60px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary};
  }
`;

const DataPointsTable = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 4px;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }

  th {
    position: sticky;
    top: 0;
    background: #fafbfc;
    padding: 6px 8px;
    text-align: left;
    font-weight: 600;
    color: ${adminTheme.colors.textSecondary};
    border-bottom: 1px solid ${adminTheme.colors.border};
  }

  td {
    padding: 4px 8px;
    border-bottom: 1px solid ${adminTheme.colors.border};
    color: ${adminTheme.colors.text};
  }

  tr:nth-child(even) {
    background: ${adminTheme.colors.rowAlt};
  }
`;

const ChartContainer = styled.div`
  margin-bottom: 16px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: ${adminTheme.colors.textMuted};
`;

const PortRow = styled.div`
  display: flex;
  gap: 12px;
`;

const componentTypes: { key: ComponentTypeEnum; label: string }[] = [
  { key: 'pump', label: 'Pump' },
  { key: 'pipe', label: 'Pipe' },
  { key: 'branch', label: 'Branch' },
  { key: 'merge', label: 'Merge' },
  { key: 'restriction', label: 'Restriction' },
  { key: 'heat_source', label: 'Heat Source' },
  { key: 'cooling_package', label: 'Cooling Package' },
];

const emptyComponent: Partial<ComponentType> = {
  name: '',
  component_type: 'pump',
  description: '',
  manufacturer: '',
  datasheet_url: '',
  input_ports: 1,
  output_ports: 1,
  default_properties: {},
  max_inlet_temperature: null,
  max_outlet_temperature: null,
};

export const ComponentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { toast } = useToast();

  const [component, setComponent] = useState<Partial<ComponentType>>(emptyComponent);
  const [curves, setCurves] = useState<CharacteristicCurve[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savingPoints, setSavingPoints] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Cooling package: available flow rates from summary + filtered data
  const [availableFlowRates, setAvailableFlowRates] = useState<number[]>([]);
  const [selectedFlowRate, setSelectedFlowRate] = useState<number | null>(null);
  const [filteredCurveData, setFilteredCurveData] = useState<Record<string, any>[]>([]);
  const [loadingCurveData, setLoadingCurveData] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    apiService.getComponent(Number(id))
      .then(data => {
        setComponent(data);
        setCurves(data.curves || []);
        // Extract available flow rates for cooling packages
        const primaryCurve = data.curves?.[0];
        if (primaryCurve?.available_flow_rates) {
          setAvailableFlowRates(primaryCurve.available_flow_rates);
          if (primaryCurve.available_flow_rates.length > 0) {
            setSelectedFlowRate(primaryCurve.available_flow_rates[0]);
          }
        }
      })
      .catch(() => toast('Failed to load component', 'error'))
      .finally(() => setLoading(false));
  }, [id, isNew, toast]);

  // Load filtered curve data when flow rate changes (cooling packages)
  useEffect(() => {
    if (selectedFlowRate === null || !id || isNew) return;
    setLoadingCurveData(true);
    apiService.getCurveData(Number(id), selectedFlowRate)
      .then(data => {
        const curveData = data[0]?.data || [];
        setFilteredCurveData(curveData);
      })
      .catch(() => toast('Failed to load curve data', 'error'))
      .finally(() => setLoadingCurveData(false));
  }, [selectedFlowRate, id, isNew, toast]);

  const handleChange = useCallback((field: string, value: any) => {
    setComponent(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await apiService.createComponent(component);
        toast('Component created', 'success');
        navigate(`/panel/components/${created.id}`, { replace: true });
      } else {
        const updated = await apiService.updateComponent(Number(id), component);
        setComponent(updated);
        toast('Component saved', 'success');
      }
    } catch (e: any) {
      toast(e.message || 'Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteComponent(Number(id));
      toast('Component deleted', 'success');
      navigate('/panel/components');
    } catch {
      toast('Failed to delete', 'error');
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const result = await apiService.uploadComponentData(Number(id), file);
      // Reload curves
      const data = await apiService.getComponent(Number(id));
      setCurves(data.curves || []);
      const pointCount = result.data?.length ?? 0;
      return { success: true, message: `Imported ${pointCount} data points` };
    } catch (e: any) {
      return { success: false, message: e.message || 'Upload failed' };
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadTemplate(Number(id));
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${component.component_type}_template.csv`;
      a.click();
    } catch {
      toast('Failed to download template', 'error');
    }
  };

  const handleSavePoints = useCallback(async (curveType: string, data: Record<string, number>[]) => {
    setSavingPoints(true);
    try {
      const primaryCurve = curves[0];
      if (primaryCurve) {
        await apiService.updateCurve(primaryCurve.id, { data });
      } else {
        await apiService.createCurve({
          component: Number(id),
          curve_type: curveType,
          data,
          metadata: { source: 'manual_entry' },
        });
      }
      // Reload component to get updated curves
      const updated = await apiService.getComponent(Number(id));
      setCurves(updated.curves || []);
      toast('Data points saved', 'success');
    } catch (e: any) {
      toast(e.message || 'Failed to save data points', 'error');
    }
    setSavingPoints(false);
  }, [curves, id, toast]);

  if (loading) return <Loading>Loading...</Loading>;

  // Find the primary curve for charting
  const primaryCurve = curves[0];
  const isCooling = component.component_type === 'cooling_package';
  const isPumpLike = ['pump', 'restriction', 'heat_source'].includes(component.component_type || '');
  const hasCharacteristics = isCooling || isPumpLike;
  // For cooling packages: use filtered data; for others: use inline data
  const curveData = isCooling ? filteredCurveData : (primaryCurve?.data || []);
  const pointCount = primaryCurve?.point_count ?? primaryCurve?.data?.length ?? 0;

  return (
    <div>
      <Header>
        <BackBtn onClick={() => navigate('/panel/components')}>
          <Icon path={mdiArrowLeft} size={0.65} />
          Components
        </BackBtn>
        <PageTitle>{isNew ? 'New Component' : component.name}</PageTitle>
        {!isNew && (
          <ActionBtn $danger onClick={() => setShowDelete(true)}>
            <Icon path={mdiDelete} size={0.6} />
            Delete
          </ActionBtn>
        )}
        <ActionBtn $primary onClick={handleSave} disabled={saving}>
          <Icon path={mdiContentSave} size={0.6} />
          {saving ? 'Saving...' : 'Save'}
        </ActionBtn>
      </Header>

      <Grid>
        <div>
          <Card>
            <CardTitle>Details</CardTitle>
            <FormGroup>
              <Label>Name</Label>
              <Input
                value={component.name || ''}
                onChange={e => handleChange('name', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Type</Label>
              <Select
                value={component.component_type || 'pump'}
                onChange={e => handleChange('component_type', e.target.value)}
                disabled={!isNew}
              >
                {componentTypes.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Manufacturer</Label>
              <Input
                value={component.manufacturer || ''}
                onChange={e => handleChange('manufacturer', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Textarea
                value={component.description || ''}
                onChange={e => handleChange('description', e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Datasheet URL</Label>
              <Input
                value={component.datasheet_url || ''}
                onChange={e => handleChange('datasheet_url', e.target.value)}
                type="url"
              />
            </FormGroup>
            <PortRow>
              <FormGroup style={{ flex: 1 }}>
                <Label>Input Ports</Label>
                <Input
                  type="number"
                  min={0}
                  value={component.input_ports ?? 1}
                  onChange={e => handleChange('input_ports', parseInt(e.target.value) || 0)}
                />
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                <Label>Output Ports</Label>
                <Input
                  type="number"
                  min={0}
                  value={component.output_ports ?? 1}
                  onChange={e => handleChange('output_ports', parseInt(e.target.value) || 0)}
                />
              </FormGroup>
            </PortRow>
            <PortRow>
              <FormGroup style={{ flex: 1 }}>
                <Label>Max Inlet Temp [°C]</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="No limit"
                  value={component.max_inlet_temperature ?? ''}
                  onChange={e => handleChange('max_inlet_temperature', e.target.value === '' ? null : parseFloat(e.target.value))}
                />
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                <Label>Max Outlet Temp [°C]</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="No limit"
                  value={component.max_outlet_temperature ?? ''}
                  onChange={e => handleChange('max_outlet_temperature', e.target.value === '' ? null : parseFloat(e.target.value))}
                />
              </FormGroup>
            </PortRow>
          </Card>
        </div>

        <div>
          {!isNew && (
            <>
              <Card style={{ marginBottom: 20 }}>
                <CardTitle>
                  Characteristic Data
                  {pointCount > 0 && (
                    <span style={{ fontWeight: 400, fontSize: 12, color: adminTheme.colors.textMuted, marginLeft: 8 }}>
                      ({pointCount.toLocaleString()} points)
                    </span>
                  )}
                </CardTitle>

                {isCooling && availableFlowRates.length > 0 && (
                  <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Flow Rate:</Label>
                    <Select
                      style={{ width: 'auto', minWidth: 120 }}
                      value={selectedFlowRate ?? ''}
                      onChange={e => setSelectedFlowRate(Number(e.target.value))}
                    >
                      {availableFlowRates.map(fr => (
                        <option key={fr} value={fr}>{fr} l/min</option>
                      ))}
                    </Select>
                    {loadingCurveData && (
                      <span style={{ fontSize: 12, color: adminTheme.colors.textMuted }}>Loading...</span>
                    )}
                  </div>
                )}

                {curveData.length > 0 && (
                  <ChartContainer>
                    {isCooling ? (
                      <CoolingDeltaTChart data={curveData} />
                    ) : isPumpLike ? (
                      <FlowPressureChart
                        data={curveData}
                        componentType={component.component_type as ComponentTypeEnum}
                      />
                    ) : (
                      <div style={{ color: adminTheme.colors.textMuted, fontSize: 13 }}>
                        No chart available for this component type
                      </div>
                    )}
                  </ChartContainer>
                )}

                {curveData.length > 0 && (
                  <DataPointsTable>
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(curveData[0]).map(key => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {curveData.map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).map((val, j) => (
                              <td key={j}>{typeof val === 'number' ? val.toFixed(2) : String(val)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataPointsTable>
                )}

                {curveData.length === 0 && !loadingCurveData && pointCount === 0 && (
                  <div style={{ color: adminTheme.colors.textMuted, fontSize: 13, marginBottom: 12 }}>
                    No characteristic data yet — enter points below or upload a file.
                  </div>
                )}

                {hasCharacteristics && !isCooling && (
                  <ManualDataEditor
                    componentType={component.component_type || ''}
                    data={curveData}
                    onSave={handleSavePoints}
                    saving={savingPoints}
                  />
                )}
              </Card>

              <Card>
                <CardTitle>Upload Data</CardTitle>
                <FileUploadZone
                  accept={isCooling ? '.xlsx,.csv' : '.csv'}
                  onUpload={handleUpload}
                />
                <div style={{ marginTop: 12 }}>
                  <ActionBtn onClick={handleDownloadTemplate}>
                    <Icon path={mdiDownload} size={0.55} />
                    Download CSV Template
                  </ActionBtn>
                </div>
              </Card>
            </>
          )}

          {isNew && (
            <Card>
              <CardTitle>Characteristic Data</CardTitle>
              <div style={{ color: adminTheme.colors.textMuted, fontSize: 13 }}>
                Save the component first, then upload characteristic data.
              </div>
            </Card>
          )}
        </div>
      </Grid>

      {showDelete && (
        <ConfirmDialog
          title="Delete Component"
          message={`Are you sure you want to delete "${component.name}"? All associated curve data will also be deleted.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};
